import { Routes, ɵEmptyOutletComponent } from '@angular/router';
import { AssetPageComponent } from '../gallery-shared/pages/asset-page/asset-page.component';
import { AssetVersionPageComponent } from '../gallery-shared/pages/asset-version-page/asset-version-page.component';
import { AllGalleryAssetsResolve } from '../gallery-shared/resolves/all-gallery-assets.resolve';
import { AssetPageActivation } from '../gallery/guards/asset-activate.guard';
import { AssetPageDeactivation } from '../gallery/guards/asset-deactivate.guard';
import { AssetVersionDeactivateGuard } from '../gallery/guards/asset-version-deactivate.guard';
import { AssetVersionGuard } from '../gallery/guards/asset-version.guard';
import { TranslationsLoadedGuard } from '../gallery/guards/translations-loaded.guard';
import { tenantProfilePageTitleResolver } from '../gallery/resolves/tenant-profile-page-title.resolver';
import { GalleryAccessGuard } from '../guards/gallery-access.guard';
import { GalleryNotFoundGuard } from '../guards/gallery-not-found.guard';
import { GalleryAdvancedGuard } from './guards/gallery-advanced.guard';
import { GalleryErrorGuard } from './guards/gallery-error.guard';
import { GalleryIdentifyGuard } from './guards/gallery-identify.guard';
import { GalleryMaintenanceGuard } from './guards/gallery-maintenance.guard';
import { GalleryReviewGuard } from './guards/gallery-review-guard';
import { GalleryUnsubscribeGuard } from './guards/gallery-unsubscribe.guard';
import { mainGalleryCanMatchGuard } from './guards/main-gallery-can-match.guard';
import { TenantProfileMaintenanceGuard } from './guards/tenant-profile-maintenance.guard';
import { TenantProfileGuard } from './guards/tenant-profile.guard';
import { ViewGalleryIdentificationGuard } from './guards/view-gallery-identification.guard';
import { ViewGalleryRedirectGuard } from './guards/view-gallery-redirect.guard';
import { ViewGalleryGuard } from './guards/view-gallery.guard';
import { GalleryErrorPageComponent } from './pages/gallery-error-page/gallery-error-page.component';
import { GalleryIdentifyPageComponent } from './pages/gallery-identify-page/gallery-identify-page.component';
import { GalleryMaintenancePageComponent } from './pages/gallery-maintenance-page/gallery-maintenance-page.component';
import { GalleryNotFoundPageComponent } from './pages/gallery-not-found-page/gallery-not-found-page.component';
import { GalleryPasswordPageComponent } from './pages/gallery-password-page/gallery-password-page.component';
import { MainGalleryProxyPageComponent } from './pages/main-gallery-proxy-page/main-gallery-proxy-page.component';
import { SubscribeToAssetComponent } from './pages/subscribe-to-asset-page/subscribe-to-asset-page.component';
import { TenantProfileMaintenancePageComponent } from './pages/tenant-profile-maintenance-page/tenant-profile-maintenance-page.component';
import { TenantProfilePageComponent } from './pages/tenant-profile-page/tenant-profile-page.component';
import { UnsubscribedBasePageComponent } from './pages/unsubscribed-base-page/unsubscribed-base-page.component';
import { UnsubscribedFromAssetComponent } from './pages/unsubscribed-from-asset-page/unsubscribed-from-asset-page.component';
import { UnsubscribedFromGalleryPageComponent } from './pages/unsubscribed-from-gallery-page/unsubscribed-from-gallery-page.component';
import { ViewGalleryAdvancedModePageComponent } from './pages/view-gallery-advanced-mode-page/view-gallery-advanced-mode-page.component';
import { ViewGalleryBasePageComponent } from './pages/view-gallery-base-page/view-gallery-base-page.component';
import { ViewGalleryPageComponent } from './pages/view-gallery-page/view-gallery-page.component';
import { publicAssetPageImageUrlResolver } from './resolves/public-asset-page-image-url.resolver';
import { publicAssetPageTitleResolver } from './resolves/public-asset-page-title.resolver';
import { publicGalleryPageDescriptionResolver } from './resolves/public-gallery-page-description.resolver';
import { publicGalleryPageImageUrlResolver } from './resolves/public-gallery-page-image-url.resolver';
import { publicGalleryPageTitleResolver } from './resolves/public-gallery-page-title.resolver';
import { TenantProfileGalleriesResolve } from './resolves/tenant-profile-galleries.resolve';
import { TenantProfileResolve } from './resolves/tenant-profile.resolve';
import { UnsubscribeGalleryResolve } from './resolves/unsubscribe-gallery.resolve';
import { ViewGalleryAccessLevelResolve } from './resolves/view-gallery-access-level.resolve';
import { ViewGalleryAssetVersionResolve } from './resolves/view-gallery-asset-version.resolve';
import { ViewGalleryAssetResolve } from './resolves/view-gallery-asset.resolve';
import { ViewGalleryIsGalleryGuestResolve } from './resolves/view-gallery-is-gallery-guest.resolve';
import { ViewGallerySectionsResolve } from './resolves/view-gallery-sections.resolve';
import { ViewGalleryResolve } from './resolves/view-gallery.resolve';

const galleryPathRoutes: Routes = [
  // gallery actions
  {
    path: 'access/:galleryPath',
    canActivate: [GalleryAccessGuard],
    resolve: { tenant: TenantProfileResolve },
    data: {
      title: 'Protected Gallery',
      description: 'This is a protected gallery which requires a password to view',
      imageUrl: 'https://picflow.com/assets/sharing/protected.png',
    },
    component: GalleryPasswordPageComponent,
    children: [
      {
        path: '**',
        component: GalleryPasswordPageComponent,
      },
    ],
  },
  {
    path: 'identify/:galleryPath',
    canActivate: [GalleryIdentifyGuard],
    resolve: {
      gallery: ViewGalleryResolve,
      title: publicGalleryPageTitleResolver,
      description: publicGalleryPageDescriptionResolver,
      imageUrl: publicGalleryPageImageUrlResolver,
    },
    component: GalleryIdentifyPageComponent,
    data: {
      title: `Gallery · Identify`,
    },
    children: [
      {
        path: '**',
        component: GalleryIdentifyPageComponent,
      },
    ],
  },
  {
    path: 'not-found/:galleryPath',
    canActivate: [GalleryNotFoundGuard],
    resolve: { tenant: TenantProfileResolve },
    component: GalleryNotFoundPageComponent,
    data: {
      title: `Gallery · Not Found`,
    },
    children: [
      {
        path: '**',
        component: GalleryNotFoundPageComponent,
      },
    ],
  },
  {
    path: 'error/:galleryPath',
    canActivate: [GalleryErrorGuard],
    resolve: { tenant: TenantProfileResolve },
    component: GalleryErrorPageComponent,
    data: {
      title: `Gallery · Error`,
    },
    children: [
      {
        path: '**',
        component: GalleryErrorPageComponent,
      },
    ],
  },
  {
    path: 'maintenance/:galleryPath',
    canActivate: [GalleryMaintenanceGuard],
    component: GalleryMaintenancePageComponent,
    data: {
      title: `Gallery · Maintenance`,
    },
    children: [
      {
        path: '**',
        component: GalleryMaintenancePageComponent,
      },
    ],
  },
  {
    path: ':galleryPath',
    component: ViewGalleryBasePageComponent,
    canActivate: [ViewGalleryGuard],
    resolve: {
      gallery: ViewGalleryResolve,
      accessLevel: ViewGalleryAccessLevelResolve,
      isGalleryGuest: ViewGalleryIsGalleryGuestResolve,
    },
    children: [
      {
        path: 'advanced',
        component: ViewGalleryAdvancedModePageComponent,
        canActivate: [GalleryAdvancedGuard],
        resolve: {
          assets: AllGalleryAssetsResolve,
          sections: ViewGallerySectionsResolve,
          title: publicGalleryPageTitleResolver,
          description: publicGalleryPageDescriptionResolver,
          imageUrl: publicGalleryPageImageUrlResolver,
        },
        children: [
          {
            path: ':assetId',
            component: AssetPageComponent,
            resolve: {
              data: ViewGalleryAssetResolve,
              title: publicAssetPageTitleResolver,
              imageUrl: publicAssetPageImageUrlResolver,
            },
            canActivate: [AssetPageActivation],
            canDeactivate: [AssetPageDeactivation],
          },
          {
            path: ':assetId/version/:versionId',
            component: AssetVersionPageComponent,
            resolve: { data: ViewGalleryAssetVersionResolve },
            canActivate: [AssetVersionGuard],
            canDeactivate: [AssetVersionDeactivateGuard],
          },
        ],
      },
      {
        path: 'embed',
        component: ViewGalleryPageComponent,
        resolve: { sections: ViewGallerySectionsResolve, embedded: () => true },
        data: {
          title: `Gallery`,
        },
        children: [
          {
            path: ':assetId',
            component: AssetPageComponent,
            resolve: { data: ViewGalleryAssetResolve },
            canActivate: [AssetPageActivation],
            canDeactivate: [AssetPageDeactivation],
          },
          {
            path: ':assetId/version/:versionId',
            component: AssetVersionPageComponent,
            resolve: { data: ViewGalleryAssetVersionResolve },
            canActivate: [AssetVersionGuard],
            canDeactivate: [AssetVersionDeactivateGuard],
          },
        ],
      },
      {
        path: 'review/:code',
        canActivate: [GalleryReviewGuard],
        children: [
          {
            path: '',
            component: ɵEmptyOutletComponent,
          },
          {
            path: '**',
            component: ɵEmptyOutletComponent,
          },
        ],
      },
      {
        path: 'subscribe/:assetId',
        component: SubscribeToAssetComponent,
        data: {
          title: `Gallery · Subscribe`,
        },
      },
      {
        path: '',
        component: ViewGalleryPageComponent,
        resolve: {
          sections: ViewGallerySectionsResolve,
          title: publicGalleryPageTitleResolver,
          description: publicGalleryPageDescriptionResolver,
          imageUrl: publicGalleryPageImageUrlResolver,
        },
        children: [
          {
            path: ':assetId',
            component: AssetPageComponent,
            resolve: {
              data: ViewGalleryAssetResolve,
              title: publicAssetPageTitleResolver,
              imageUrl: publicAssetPageImageUrlResolver,
            },
            canActivate: [AssetPageActivation],
            canDeactivate: [AssetPageDeactivation],
          },
          {
            path: ':assetId/version/:versionId',
            component: AssetVersionPageComponent,
            resolve: { data: ViewGalleryAssetVersionResolve },
            canActivate: [AssetVersionGuard],
            canDeactivate: [AssetVersionDeactivateGuard],
          },
        ],
      },
    ],
  },
];

export const routes: Routes = [
  {
    path: '',
    canActivate: [ViewGalleryRedirectGuard, ViewGalleryIdentificationGuard, TranslationsLoadedGuard],
    children: [
      // tenant pages
      {
        path: '',
        component: TenantProfilePageComponent,
        canActivate: [TenantProfileGuard],
        resolve: {
          tenant: TenantProfileResolve,
          galleries: TenantProfileGalleriesResolve,
          title: tenantProfilePageTitleResolver,
        },
        data: {
          imageUrl: '',
        },
      },
      {
        path: 'maintenance',
        component: TenantProfileMaintenancePageComponent,
        canActivate: [TenantProfileMaintenanceGuard],
        data: {
          title: `Maintenance`,
        },
      },
      // gallery
      // unsubscribe
      {
        path: ':galleryId/unsubscribe',
        component: UnsubscribedBasePageComponent,
        canActivate: [GalleryUnsubscribeGuard],
        resolve: { gallery: UnsubscribeGalleryResolve },
        data: {
          title: `Gallery · Unsubscribe`,
        },
        children: [
          {
            path: '',
            canActivate: [ViewGalleryGuard],
            component: UnsubscribedFromGalleryPageComponent,
          },
          {
            path: ':assetId',
            component: UnsubscribedFromAssetComponent,
          },
        ],
      },
      // sub gallery
      {
        path: ':mainGalleryPath',
        component: MainGalleryProxyPageComponent,
        canMatch: [mainGalleryCanMatchGuard],
        children: [...galleryPathRoutes],
      },
      // main gallery
      ...galleryPathRoutes,
    ],
  },
];
